import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Drawer,
  Avatar,
  Divider,
  Badge,
  Tree,
  Tooltip,
  Popconfirm,
  InputNumber,
  Spin,
  Empty,
  Alert,
  Dropdown,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined,
  TeamOutlined,
  ApartmentOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  BankOutlined,
  IdcardOutlined,
  StopOutlined,
  UndoOutlined,
  CoffeeOutlined,
  PlayCircleOutlined,
  DownOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  departmentService,
  positionService,
  employeeService,
  salaryStructureService,
  Department,
  Position,
  Employee,
  SalaryStructure,
  CreateDepartmentInput,
  CreatePositionInput,
  CreateEmployeeInput,
  DepartmentStats,
  PositionStats,
  EmployeeStats
} from '../../../../services/hrService';
import RegisterToZkModal from './RegisterToZkModal';
import { zkMappingApi } from '../../services/zkService';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const POSITION_LEVELS = [
  { value: 'ENTRY', label: 'Entry Level' },
  { value: 'JUNIOR', label: 'Junior' },
  { value: 'MID', label: 'Mid-Level' },
  { value: 'SENIOR', label: 'Senior' },
  { value: 'LEAD', label: 'Lead' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'DIRECTOR', label: 'Director' },
  { value: 'VP', label: 'Vice President' },
  { value: 'C_LEVEL', label: 'C-Level' },
];

const EMPLOYMENT_TYPES = [
  { value: 'FULL_TIME', label: 'Full-time' },
  { value: 'PART_TIME', label: 'Part-time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'INTERN', label: 'Intern' },
  { value: 'CONSULTANT', label: 'Consultant' },
];

const EMPLOYEE_STATUSES = [
  { value: 'ACTIVE', label: 'Active', color: 'green' },
  { value: 'INACTIVE', label: 'Inactive', color: 'default' },
  { value: 'ON_LEAVE', label: 'On Leave', color: 'orange' },
  { value: 'TERMINATED', label: 'Terminated', color: 'red' },
];

const TERMINATION_REASONS = [
  { value: 'RESIGNED', label: 'Resigned' },
  { value: 'RETIRED', label: 'Retired' },
  { value: 'FIRED', label: 'Fired / Dismissed' },
  { value: 'CONTRACT_ENDED', label: 'Contract Ended' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * How an employee's name is displayed: surname first.
 *
 * These are Hong Kong Chinese names, where the surname leads and no comma is
 * used — "Chiu Hong Chan" is displayed as "Chan Chiu Hong". Everywhere a name
 * is shown in this screen goes through here, so the list, the manager pickers,
 * the detail panel and the search box can never disagree with each other.
 */
const employeeName = (emp?: { firstName?: string; middleName?: string | null; lastName?: string } | null): string => {
  if (!emp) return '';
  return [emp.lastName, emp.firstName, emp.middleName]
    .map(part => (part || '').trim())
    .filter(Boolean)
    .join(' ');
};

const EmployeeManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('employees');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [salaryStructures, setSalaryStructures] = useState<SalaryStructure[]>([]);

  // Stats states
  const [employeeStats, setEmployeeStats] = useState<EmployeeStats | null>(null);
  const [departmentStats, setDepartmentStats] = useState<DepartmentStats | null>(null);
  const [positionStats, setPositionStats] = useState<PositionStats | null>(null);

  // Modal states
  const [isEmployeeModalVisible, setIsEmployeeModalVisible] = useState(false);
  const [isDepartmentModalVisible, setIsDepartmentModalVisible] = useState(false);
  const [isPositionModalVisible, setIsPositionModalVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [zkModalEmployee, setZkModalEmployee] = useState<Employee | null>(null);
  // Set of Employee.id values that already have ≥1 active ZK device mapping.
  // Used to hide the "Register ZK" button for already-registered employees.
  const [zkLinkedEmployeeIds, setZkLinkedEmployeeIds] = useState<Set<string>>(new Set());

  // Termination modal — captures reason + dates + ZK device cleanup choice.
  const [terminateEmployee, setTerminateEmployee] = useState<Employee | null>(null);
  const [terminateForm] = Form.useForm();

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    departmentId: '',
    positionId: '',
    status: '',
    employmentType: ''
  });

  // Forms
  const [employeeForm] = Form.useForm();
  const [departmentForm] = Form.useForm();
  const [positionForm] = Form.useForm();

  // Load the set of Employee IDs that are already registered to ≥1 active ZK device
  const loadZkLinkedIds = useCallback(async () => {
    try {
      const mappings = await zkMappingApi.getAll();
      const linked = new Set<string>();
      for (const m of mappings) {
        if (m.employeeId && m.isActive) linked.add(m.employeeId);
      }
      setZkLinkedEmployeeIds(linked);
    } catch (e) {
      // Non-fatal — just means the Register ZK button will show for all employees
      console.warn('Failed to load ZK mappings:', e);
    }
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [empData, deptData, posData, salStructData] = await Promise.all([
        employeeService.getAll(),
        departmentService.getAll(),
        positionService.getAll(),
        salaryStructureService.getAll()
      ]);
      setEmployees(empData || []);
      setDepartments(deptData || []);
      setPositions(posData || []);
      setSalaryStructures(salStructData || []);
      loadZkLinkedIds();
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [loadZkLinkedIds]);

  const fetchStats = useCallback(async () => {
    try {
      const [empStats, deptStats, posStats] = await Promise.all([
        employeeService.getStats(),
        departmentService.getStats(),
        positionService.getStats()
      ]);
      setEmployeeStats(empStats);
      setDepartmentStats(deptStats);
      setPositionStats(posStats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [fetchData, fetchStats]);

  // Status helpers
  const getStatusColor = (status: string) => {
    const found = EMPLOYEE_STATUSES.find(s => s.value === status);
    return found?.color || 'default';
  };

  const getLevelLabel = (level: string) => {
    const found = POSITION_LEVELS.find(l => l.value === level);
    return found?.label || level;
  };

  const getEmploymentTypeLabel = (type: string) => {
    const found = EMPLOYMENT_TYPES.find(t => t.value === type);
    return found?.label || type;
  };

  // Employee handlers
  const handleAddEmployee = async (values: any) => {
    try {
      setLoading(true);
      const data: CreateEmployeeInput = {
        ...values,
        hireDate: values.hireDate?.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
        dateOfBirth: values.dateOfBirth?.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
        bankAccount: values.bankName ? {
          bankName: values.bankName,
          bankCode: values.bankCode || '',
          accountNumber: values.accountNumber || ''
        } : undefined,
        // An untouched address block yields an object of empty strings. Send
        // nothing at all in that case, so employees edited for other reasons
        // don't gain a meaningless address record.
        address: values.address && Object.values(values.address).some(v => String(v ?? '').trim())
          ? values.address
          : undefined
      };

      if (editingEmployee) {
        await employeeService.update(editingEmployee.id, data);
        message.success('Employee updated successfully');
      } else {
        await employeeService.create(data);
        message.success('Employee added successfully');
      }

      setIsEmployeeModalVisible(false);
      setEditingEmployee(null);
      employeeForm.resetFields();
      fetchData();
      fetchStats();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to save employee');
    } finally {
      setLoading(false);
    }
  };

  // Lifecycle handlers — share the same fetchData/fetchStats refresh.
  // (The old handleDeleteEmployee that hit DELETE /employees/v2/:id was
  // replaced by openTerminateModal — the new flow captures reason + dates
  // + ZK device cleanup instead of a silent soft-delete.)
  const handleSetActive = async (emp: Employee) => {
    try {
      if (emp.status === 'TERMINATED') {
        await employeeService.reactivate(emp.id);
        message.success('Employee reactivated');
      } else if (emp.status === 'ON_LEAVE') {
        await employeeService.returnFromLeave(emp.id);
        message.success('Employee returned from leave');
      } else {
        return;
      }
      fetchData();
      fetchStats();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleSetOnLeave = async (emp: Employee) => {
    try {
      await employeeService.setOnLeave(emp.id);
      message.success('Employee marked as on leave');
      fetchData();
      fetchStats();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to update status');
    }
  };

  // Open the termination modal — capture reason + dates + ZK cleanup choice.
  const openTerminateModal = (emp: Employee) => {
    terminateForm.resetFields();
    terminateForm.setFieldsValue({
      terminationReason: 'RESIGNED',
      terminationDate: dayjs(),
      lastWorkingDay: dayjs(),
      removeFromZkDevices: true,
    });
    setTerminateEmployee(emp);
  };

  const handleTerminateSubmit = async (values: any) => {
    if (!terminateEmployee) return;
    try {
      setLoading(true);
      const result = await employeeService.terminate(terminateEmployee.id, {
        terminationReason: values.terminationReason,
        terminationNote: values.terminationNote || undefined,
        terminationDate: values.terminationDate
          ? values.terminationDate.format('YYYY-MM-DD')
          : undefined,
        lastWorkingDay: values.lastWorkingDay
          ? values.lastWorkingDay.format('YYYY-MM-DD')
          : undefined,
        removeFromZkDevices: !!values.removeFromZkDevices,
      });

      // Surface ZK device cleanup outcomes — never blocks the termination
      // itself, but the user wants to know if a device was unreachable.
      if (result.zk && values.removeFromZkDevices) {
        if (result.zk.attempted === 0) {
          message.success('Employee terminated. No ZK device mappings to clean up.');
        } else if (result.zk.errors.length === 0) {
          message.success(
            `Employee terminated. Removed from ${result.zk.removed}/${result.zk.attempted} ZK device(s).`
          );
        } else {
          message.warning(
            `Employee terminated, but ZK cleanup hit ${result.zk.errors.length} error(s). Local mappings deleted; check device(s) manually.`
          );
        }
      } else {
        message.success('Employee terminated');
      }

      setTerminateEmployee(null);
      fetchData();
      fetchStats();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to terminate employee');
    } finally {
      setLoading(false);
    }
  };

  // Department handlers
  const handleAddDepartment = async (values: any) => {
    try {
      setLoading(true);
      const data: CreateDepartmentInput = {
        code: values.code,
        name: values.name,
        description: values.description,
        parentId: values.parentId || undefined,
        managerId: values.managerId || undefined,
        budget: values.budget,
        costCenter: values.costCenter
      };

      if (editingDepartment) {
        await departmentService.update(editingDepartment.id, data);
        message.success('Department updated successfully');
      } else {
        await departmentService.create(data);
        message.success('Department added successfully');
      }

      setIsDepartmentModalVisible(false);
      setEditingDepartment(null);
      departmentForm.resetFields();
      fetchData();
      fetchStats();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to save department');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDepartment = async (id: string) => {
    try {
      await departmentService.delete(id);
      message.success('Department deleted successfully');
      fetchData();
      fetchStats();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to delete department');
    }
  };

  // Position handlers
  const handleAddPosition = async (values: any) => {
    try {
      setLoading(true);
      const data: CreatePositionInput = {
        code: values.code,
        title: values.title,
        description: values.description,
        departmentId: values.departmentId,
        level: values.level,
        grading: values.grading,
        employmentType: values.employmentType,
        minSalary: values.minSalary,
        maxSalary: values.maxSalary,
        annualLeave: values.annualLeave,
        responsibilities: values.responsibilities,
        qualifications: values.qualifications
      };

      if (editingPosition) {
        await positionService.update(editingPosition.id, data);
        message.success('Position updated successfully');
      } else {
        await positionService.create(data);
        message.success('Position added successfully');
      }

      setIsPositionModalVisible(false);
      setEditingPosition(null);
      positionForm.resetFields();
      fetchData();
      fetchStats();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to save position');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePosition = async (id: string) => {
    try {
      await positionService.delete(id);
      message.success('Position deleted successfully');
      fetchData();
      fetchStats();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to delete position');
    }
  };

  // Filter employees
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = searchText === '' ||
      employeeName(emp).toLowerCase().includes(searchText.toLowerCase()) ||
      (emp.employeeId && emp.employeeId.toLowerCase().includes(searchText.toLowerCase())) ||
      (emp.email && emp.email.toLowerCase().includes(searchText.toLowerCase()));
    const matchesDepartment = !filters.departmentId || emp.departmentId === filters.departmentId;
    const matchesPosition = !filters.positionId || emp.positionId === filters.positionId;
    const matchesStatus = !filters.status || emp.status === filters.status;
    const matchesType = !filters.employmentType || emp.employmentType === filters.employmentType;
    return matchesSearch && matchesDepartment && matchesPosition && matchesStatus && matchesType;
  });

  // Table columns
  const employeeColumns: ColumnsType<Employee> = [
    {
      title: 'Employee',
      key: 'employee',
      fixed: 'left',
      width: 250,
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} src={record.profilePhoto} />
          <div>
            <div style={{ fontWeight: 500 }}>{employeeName(record)}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.employeeId}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 200,
      render: (_, record) => (
        <div>
          <div><MailOutlined /> {record.email}</div>
          {record.phone && <div><PhoneOutlined /> {record.phone}</div>}
        </div>
      )
    },
    {
      title: 'Department',
      dataIndex: 'departmentId',
      key: 'department',
      width: 150,
      render: (deptId) => {
        const dept = departments.find(d => d.id === deptId);
        return dept ? <Tag icon={<ApartmentOutlined />}>{dept.name}</Tag> : '-';
      }
    },
    {
      title: 'Position',
      dataIndex: 'positionId',
      key: 'position',
      width: 180,
      render: (posId) => {
        const pos = positions.find(p => p.id === posId);
        return pos ? pos.title : '-';
      }
    },
    {
      title: 'Type',
      dataIndex: 'employmentType',
      key: 'employmentType',
      width: 100,
      render: (type) => type ? <Tag>{getEmploymentTypeLabel(type)}</Tag> : '-'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => {
        // Status tag doubles as the status-change menu — only options that
        // make sense for the current state are shown. Terminate always opens
        // the modal so reason + dates + ZK cleanup can be captured.
        const items = [
          status !== 'ACTIVE' && {
            key: 'active',
            icon: status === 'TERMINATED' ? <UndoOutlined /> : <PlayCircleOutlined />,
            label: status === 'TERMINATED' ? 'Reactivate (set Active)' : 'Set Active',
            onClick: () => handleSetActive(record),
          },
          status === 'ACTIVE' && {
            key: 'on-leave',
            icon: <CoffeeOutlined />,
            label: 'Set On Leave',
            onClick: () => handleSetOnLeave(record),
          },
          status !== 'TERMINATED' && {
            key: 'terminate',
            icon: <StopOutlined />,
            danger: true,
            label: 'Terminate…',
            onClick: () => openTerminateModal(record),
          },
        ].filter(Boolean) as any[];

        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Tag
              color={getStatusColor(status)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {status} <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
            </Tag>
          </Dropdown>
        );
      }
    },
    {
      title: 'Hire Date',
      dataIndex: 'hireDate',
      key: 'hireDate',
      width: 120,
      render: (date) => date ? dayjs(date).format('MMM DD, YYYY') : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 420,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setSelectedEmployee(record)}>
            View
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => {
            setEditingEmployee(record);
            employeeForm.setFieldsValue({
              ...record,
              salaryStructureId: record.salaryStructureId,
              hireDate: record.hireDate ? dayjs(record.hireDate) : undefined,
              dateOfBirth: record.dateOfBirth ? dayjs(record.dateOfBirth) : undefined,
              bankName: record.bankAccount?.bankName,
              bankCode: record.bankAccount?.bankCode,
              accountNumber: record.bankAccount?.accountNumber,
            });
            setIsEmployeeModalVisible(true);
          }}>
            Edit
          </Button>
          {zkLinkedEmployeeIds.has(record.id) ? (
            <Tooltip title="Already registered on at least one ZK device">
              <Tag icon={<IdcardOutlined />} color="success" style={{ margin: 0 }}>
                ZK Linked
              </Tag>
            </Tooltip>
          ) : (
            <Tooltip title="Register this employee to all ZK attendance devices">
              <Button
                size="small"
                type="primary"
                ghost
                icon={<IdcardOutlined />}
                onClick={() => setZkModalEmployee(record)}
              >
                Register ZK
              </Button>
            </Tooltip>
          )}
          {record.status === 'TERMINATED' ? (
            <Tooltip title="Reactivate to set status back to Active">
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={() => handleSetActive(record)}
              >
                Reactivate
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Capture reason, end date, and clean up ZK device(s)">
              <Button
                size="small"
                icon={<StopOutlined />}
                danger
                onClick={() => openTerminateModal(record)}
              >
                Terminate
              </Button>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const departmentColumns: ColumnsType<Department> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 100
    },
    {
      title: 'Department',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <ApartmentOutlined />
          <span>{text}</span>
          {!record.isActive && <Tag color="red">Inactive</Tag>}
        </Space>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Manager',
      dataIndex: 'managerId',
      key: 'manager',
      render: (managerId) => {
        const manager = employees.find(emp => emp.id === managerId);
        return manager ? employeeName(manager) : '-';
      }
    },
    {
      title: 'Cost Center',
      dataIndex: 'costCenter',
      key: 'costCenter',
      render: (text) => text || '-'
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => {
            setEditingDepartment(record);
            departmentForm.setFieldsValue(record);
            setIsDepartmentModalVisible(true);
          }} />
          <Popconfirm title="Delete this department?" onConfirm={() => handleDeleteDepartment(record.id)}>
            <Button type="text" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const positionColumns: ColumnsType<Position> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 100
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: 'Department',
      dataIndex: 'departmentId',
      key: 'department',
      render: (deptId) => {
        const dept = departments.find(d => d.id === deptId);
        return dept?.name || '-';
      }
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      render: (level) => <Tag color="blue">{getLevelLabel(level)}</Tag>
    },
    {
      title: 'Salary Range',
      key: 'salary',
      render: (_, record) => {
        if (record.minSalary || record.maxSalary) {
          return `$${(record.minSalary || 0).toLocaleString()} - $${(record.maxSalary || 0).toLocaleString()}`;
        }
        return '-';
      }
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => {
            setEditingPosition(record);
            positionForm.setFieldsValue(record);
            setIsPositionModalVisible(true);
          }} />
          <Popconfirm title="Delete this position?" onConfirm={() => handleDeletePosition(record.id)}>
            <Button type="text" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Org chart tree data
  const getOrgChartData = () => {
    return departments.map(dept => ({
      title: (
        <Space>
          <ApartmentOutlined />
          <span>{dept.name}</span>
          <Badge count={employees.filter(e => e.departmentId === dept.id).length} showZero style={{ backgroundColor: '#1890ff' }} />
        </Space>
      ),
      key: dept.id,
      children: positions
        .filter(p => p.departmentId === dept.id)
        .map(pos => ({
          title: (
            <Space>
              <TeamOutlined />
              <span>{pos.title}</span>
              <Badge count={employees.filter(e => e.positionId === pos.id).length} showZero style={{ backgroundColor: '#52c41a' }} />
            </Space>
          ),
          key: pos.id
        }))
    }));
  };

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={<Button onClick={fetchData}>Retry</Button>}
      />
    );
  }

  return (
    <Spin spinning={loading}>
      <div style={{ padding: '24px' }}>
        {/* Header */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>Employee Management</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => { fetchData(); fetchStats(); }}>
                Refresh
              </Button>
              <Button icon={<DownloadOutlined />}>Export</Button>
              <Button type="primary" icon={<UserAddOutlined />} onClick={() => {
                setEditingEmployee(null);
                employeeForm.resetFields();
                setIsEmployeeModalVisible(true);
              }}>
                Add Employee
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Stats Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Employees"
                value={employeeStats?.total || employees.length}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active"
                value={employeeStats?.active || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Departments"
                value={departmentStats?.total || departments.length}
                prefix={<ApartmentOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Positions"
                value={positionStats?.total || positions.length}
                prefix={<BankOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Tabs */}
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab={<span><TeamOutlined /> Employees</span>} key="employees">
            <Card>
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={6}>
                  <Input
                    placeholder="Search by name, ID, email..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Select
                    placeholder="Department"
                    style={{ width: '100%' }}
                    value={filters.departmentId || undefined}
                    onChange={(value) => setFilters({ ...filters, departmentId: value })}
                    allowClear
                  >
                    {departments.map(dept => (
                      <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Select
                    placeholder="Position"
                    style={{ width: '100%' }}
                    value={filters.positionId || undefined}
                    onChange={(value) => setFilters({ ...filters, positionId: value })}
                    allowClear
                  >
                    {positions.map(pos => (
                      <Option key={pos.id} value={pos.id}>{pos.title}</Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Select
                    placeholder="Status"
                    style={{ width: '100%' }}
                    value={filters.status || undefined}
                    onChange={(value) => setFilters({ ...filters, status: value })}
                    allowClear
                  >
                    {EMPLOYEE_STATUSES.map(s => (
                      <Option key={s.value} value={s.value}>{s.label}</Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Select
                    placeholder="Employment Type"
                    style={{ width: '100%' }}
                    value={filters.employmentType || undefined}
                    onChange={(value) => setFilters({ ...filters, employmentType: value })}
                    allowClear
                  >
                    {EMPLOYMENT_TYPES.map(t => (
                      <Option key={t.value} value={t.value}>{t.label}</Option>
                    ))}
                  </Select>
                </Col>
              </Row>

              {filteredEmployees.length === 0 && !loading ? (
                <Empty description="No employees found" />
              ) : (
                <Table
                  columns={employeeColumns}
                  dataSource={filteredEmployees}
                  rowKey="id"
                  pagination={{ defaultPageSize: 10, showSizeChanger: true, showTotal: (total) => `Total ${total} employees` }}
                  scroll={{ x: 1200 }}
                />
              )}
            </Card>
          </Tabs.TabPane>

          <Tabs.TabPane tab={<span><ApartmentOutlined /> Departments</span>} key="departments">
            <Card
              extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                  setEditingDepartment(null);
                  departmentForm.resetFields();
                  setIsDepartmentModalVisible(true);
                }}>
                  Add Department
                </Button>
              }
            >
              <Table
                columns={departmentColumns}
                dataSource={departments}
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            </Card>
          </Tabs.TabPane>

          <Tabs.TabPane tab={<span><BankOutlined /> Positions</span>} key="positions">
            <Card
              extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                  setEditingPosition(null);
                  positionForm.resetFields();
                  setIsPositionModalVisible(true);
                }}>
                  Add Position
                </Button>
              }
            >
              <Table
                columns={positionColumns}
                dataSource={positions}
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            </Card>
          </Tabs.TabPane>

          <Tabs.TabPane tab={<span><ApartmentOutlined /> Org Chart</span>} key="org-chart">
            <Card title="Organization Structure">
              {departments.length === 0 ? (
                <Empty description="No departments found" />
              ) : (
                <Tree
                  showLine={{ showLeafIcon: false }}
                  defaultExpandAll
                  treeData={getOrgChartData()}
                />
              )}
            </Card>
          </Tabs.TabPane>
        </Tabs>

        {/* Add/Edit Employee Modal */}
        <Modal
          title={editingEmployee ? 'Edit Employee' : 'Add New Employee'}
          open={isEmployeeModalVisible}
          onCancel={() => {
            setIsEmployeeModalVisible(false);
            setEditingEmployee(null);
            employeeForm.resetFields();
          }}
          footer={null}
          width={800}
          destroyOnClose
        >
          <Form form={employeeForm} layout="vertical" onFinish={handleAddEmployee}>
            <Title level={5}>Basic Information</Title>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="employeeId" label="Employee ID" rules={[{ required: true }]}>
                  <Input placeholder="e.g., EMP001" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
                  <Input prefix={<MailOutlined />} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="phone" label="Phone">
                  <Input prefix={<PhoneOutlined />} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="dateOfBirth" label="Date of Birth">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="hkidNo" label="HKID No">
                  <Input placeholder="e.g., A123456(7)" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="gender" label="Gender">
                  <Select placeholder="Select gender">
                    <Option value="MALE">Male</Option>
                    <Option value="FEMALE">Female</Option>
                    <Option value="OTHER">Other</Option>
                    <Option value="PREFER_NOT_TO_SAY">Prefer not to say</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="nationality" label="Nationality">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="maritalStatus" label="Marital Status">
                  <Select placeholder="Select marital status">
                    <Option value="SINGLE">Single</Option>
                    <Option value="MARRIED">Married</Option>
                    <Option value="DIVORCED">Divorced</Option>
                    <Option value="WIDOWED">Widowed</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Divider />
            <Title level={5}>Address</Title>
            {/* Stored as one JSON column on the employee, so the field names are
                nested — Ant assembles values.address from these automatically,
                and setFieldsValue fills them back from the saved object. */}
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name={['address', 'street']} label="Street">
                  <Input placeholder="Flat / floor, building, street" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name={['address', 'city']} label="City / District">
                  <Input placeholder="e.g. Kwun Tong" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['address', 'state']} label="Region / State">
                  <Input placeholder="e.g. Kowloon" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['address', 'postalCode']} label="Postal Code">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name={['address', 'country']} label="Country">
                  <Input placeholder="e.g. Hong Kong" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />
            <Title level={5}>Employment Details</Title>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="departmentId" label="Department">
                  <Select placeholder="Select department" allowClear>
                    {departments.map(dept => (
                      <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="positionId" label="Position">
                  <Select placeholder="Select position" allowClear>
                    {positions.map(pos => (
                      <Option key={pos.id} value={pos.id}>{pos.title}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="employmentType" label="Employment Type">
                  <Select placeholder="Select type">
                    {EMPLOYMENT_TYPES.map(t => (
                      <Option key={t.value} value={t.value}>{t.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="hireDate" label="Hire Date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="workLocation" label="Work Location">
                  <Select placeholder="Select location">
                    <Option value="OFFICE">Office</Option>
                    <Option value="REMOTE">Remote</Option>
                    <Option value="HYBRID">Hybrid</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="salary" label="Base Salary" rules={[{ required: true }]}>
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="salaryType" label="Salary Type">
                  <Select placeholder="Select salary type">
                    <Option value="MONTHLY">Monthly</Option>
                    <Option value="HOURLY">Hourly</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="salaryStructureId" label="Salary Structure">
                  <Select placeholder="Select salary structure" allowClear>
                    {salaryStructures.filter(s => s.isActive).map(struct => (
                      <Option key={struct.id} value={struct.id}>
                        {struct.name} ({struct.currency} {Number(struct.baseSalary).toLocaleString()})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="managerId" label="Manager">
                  <Select
                    placeholder="Select or type a name to search"
                    allowClear
                    showSearch
                    // Options render as elements, so Ant cannot search them on
                    // its own — match against the option's text instead.
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {employees.filter(e => e.id !== editingEmployee?.id).map(emp => (
                      <Option key={emp.id} value={emp.id}>{employeeName(emp)}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Divider />
            <Title level={5}>Bank Details (Optional)</Title>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="bankName" label="Bank Name">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="bankCode" label="Bank Code">
                  <Input placeholder="e.g., 004, 012" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="accountNumber" label="Account Number">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {editingEmployee ? 'Update Employee' : 'Add Employee'}
                </Button>
                <Button onClick={() => {
                  setIsEmployeeModalVisible(false);
                  setEditingEmployee(null);
                  employeeForm.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Add/Edit Department Modal */}
        <Modal
          title={editingDepartment ? 'Edit Department' : 'Add New Department'}
          open={isDepartmentModalVisible}
          onCancel={() => {
            setIsDepartmentModalVisible(false);
            setEditingDepartment(null);
            departmentForm.resetFields();
          }}
          footer={null}
          width={500}
          destroyOnClose
        >
          <Form form={departmentForm} layout="vertical" onFinish={handleAddDepartment}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="code" label="Code" rules={[{ required: true }]}>
                  <Input placeholder="e.g., ENG" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                  <Input placeholder="e.g., Engineering" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="description" label="Description">
              <TextArea rows={2} />
            </Form.Item>
            <Form.Item name="parentId" label="Parent Department">
              <Select placeholder="Select parent (optional)" allowClear>
                {departments.filter(d => d.id !== editingDepartment?.id).map(dept => (
                  <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="managerId" label="Manager">
              <Select
                    placeholder="Select or type a name to search"
                    allowClear
                    showSearch
                    // Options render as elements, so Ant cannot search them on
                    // its own — match against the option's text instead.
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id}>{employeeName(emp)}</Option>
                ))}
              </Select>
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="budget" label="Budget">
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    // Strip the "$ " prefix and commas the formatter adds so the
                    // form submits a real number instead of "$ 5,000" — Zod's
                    // z.coerce.number() can't recover from that string.
                    parser={v => (v ? v.replace(/\$\s?|(,*)/g, '') : '') as any}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="costCenter" label="Cost Center">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item style={{ marginBottom: 0 }}>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {editingDepartment ? 'Update' : 'Add'} Department
                </Button>
                <Button onClick={() => { setIsDepartmentModalVisible(false); departmentForm.resetFields(); }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Add/Edit Position Modal */}
        <Modal
          title={editingPosition ? 'Edit Position' : 'Add New Position'}
          open={isPositionModalVisible}
          onCancel={() => {
            setIsPositionModalVisible(false);
            setEditingPosition(null);
            positionForm.resetFields();
          }}
          footer={null}
          width={600}
          destroyOnClose
        >
          <Form form={positionForm} layout="vertical" onFinish={handleAddPosition}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="code" label="Code" rules={[{ required: true }]}>
                  <Input placeholder="e.g., SWE" />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                  <Input placeholder="e.g., Software Engineer" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="description" label="Description">
              <TextArea rows={2} />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="departmentId" label="Department" rules={[{ required: true }]}>
                  <Select placeholder="Select department">
                    {departments.map(dept => (
                      <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="level" label="Level" rules={[{ required: true }]}>
                  <Select placeholder="Select level">
                    {POSITION_LEVELS.map(l => (
                      <Option key={l.value} value={l.value}>{l.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="grading" label="Grading">
                  <Input placeholder="Enter position grading (e.g., A1, B2)" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="employmentType" label="Employment Type">
                  <Select placeholder="Select type">
                    {EMPLOYMENT_TYPES.map(t => (
                      <Option key={t.value} value={t.value}>{t.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="minSalary" label="Min Salary">
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    // Strip the "$ " prefix and commas the formatter adds so the
                    // form submits a real number instead of "$ 5,000" — Zod's
                    // z.coerce.number() can't recover from that string.
                    parser={v => (v ? v.replace(/\$\s?|(,*)/g, '') : '') as any}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="maxSalary" label="Max Salary">
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    // Strip the "$ " prefix and commas the formatter adds so the
                    // form submits a real number instead of "$ 5,000" — Zod's
                    // z.coerce.number() can't recover from that string.
                    parser={v => (v ? v.replace(/\$\s?|(,*)/g, '') : '') as any}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="annualLeave" label="Annual Leave (Days)">
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="Enter annual leave days"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="responsibilities" label="Responsibilities">
              <TextArea rows={2} placeholder="Key responsibilities..." />
            </Form.Item>
            <Form.Item name="qualifications" label="Qualifications">
              <TextArea rows={2} placeholder="Required qualifications..." />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {editingPosition ? 'Update' : 'Add'} Position
                </Button>
                <Button onClick={() => { setIsPositionModalVisible(false); positionForm.resetFields(); }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Employee Details Drawer */}
        <Drawer
          title="Employee Details"
          placement="right"
          width={500}
          open={!!selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
        >
          {selectedEmployee && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Avatar size={80} icon={<UserOutlined />} src={selectedEmployee.profilePhoto} />
                <Title level={4} style={{ marginTop: 16, marginBottom: 4 }}>
                  {employeeName(selectedEmployee)}
                </Title>
                <Text type="secondary">{selectedEmployee.employeeId}</Text>
                <div style={{ marginTop: 8 }}>
                  <Tag color={getStatusColor(selectedEmployee.status)}>{selectedEmployee.status}</Tag>
                </div>
              </div>

              <Divider />

              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text type="secondary">Email</Text>
                  <div>{selectedEmployee.email}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Phone</Text>
                  <div>{selectedEmployee.phone || '-'}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Department</Text>
                  <div>{departments.find(d => d.id === selectedEmployee.departmentId)?.name || '-'}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Position</Text>
                  <div>{positions.find(p => p.id === selectedEmployee.positionId)?.title || '-'}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Hire Date</Text>
                  <div>{selectedEmployee.hireDate ? dayjs(selectedEmployee.hireDate).format('MMM DD, YYYY') : '-'}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Employment Type</Text>
                  <div>{selectedEmployee.employmentType ? getEmploymentTypeLabel(selectedEmployee.employmentType) : '-'}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Work Location</Text>
                  <div>{selectedEmployee.workLocation || '-'}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Salary</Text>
                  <div>${selectedEmployee.salary?.toLocaleString() || '-'}</div>
                </Col>
                <Col span={24}>
                  <Text type="secondary">Address</Text>
                  <div>
                    {(() => {
                      const a = (selectedEmployee as any).address;
                      const line = a && [a.street, a.city, a.state, a.postalCode, a.country]
                        .map((x: any) => (x || '').trim()).filter(Boolean).join(', ');
                      return line || '-';
                    })()}
                  </div>
                </Col>
              </Row>

              <Divider />

              <Space style={{ width: '100%', justifyContent: 'center' }}>
                <Button icon={<EditOutlined />} onClick={() => {
                  setSelectedEmployee(null);
                  setEditingEmployee(selectedEmployee);
                  employeeForm.setFieldsValue({
                    ...selectedEmployee,
                    hireDate: selectedEmployee.hireDate ? dayjs(selectedEmployee.hireDate) : undefined,
                    dateOfBirth: selectedEmployee.dateOfBirth ? dayjs(selectedEmployee.dateOfBirth) : undefined,
                  });
                  setIsEmployeeModalVisible(true);
                }}>
                  Edit
                </Button>
              </Space>
            </div>
          )}
        </Drawer>

        <RegisterToZkModal
          visible={!!zkModalEmployee}
          onClose={() => {
            setZkModalEmployee(null);
            loadZkLinkedIds(); // refresh so button swaps to "ZK Linked" tag immediately
          }}
          employee={zkModalEmployee ? {
            id: zkModalEmployee.id,
            employeeId: zkModalEmployee.employeeId,
            firstName: zkModalEmployee.firstName,
            lastName: zkModalEmployee.lastName
          } : null}
        />

        {/* Termination Modal — capture reason + dates and optionally remove
            the employee from every ZK device they're registered on. */}
        <Modal
          title={
            terminateEmployee
              ? `Terminate ${employeeName(terminateEmployee)}`
              : 'Terminate Employee'
          }
          open={!!terminateEmployee}
          onCancel={() => setTerminateEmployee(null)}
          footer={null}
          width={520}
          destroyOnClose
        >
          <Form form={terminateForm} layout="vertical" onFinish={handleTerminateSubmit}>
            <Form.Item
              name="terminationReason"
              label="Reason"
              rules={[{ required: true, message: 'Please pick a reason' }]}
            >
              <Select placeholder="Select reason">
                {TERMINATION_REASONS.map(r => (
                  <Option key={r.value} value={r.value}>{r.label}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="terminationNote"
              label="Note (optional)"
              tooltip="Required when Reason is 'Other'; otherwise free-form context that's saved alongside the reason."
            >
              <TextArea
                rows={2}
                placeholder="e.g., End of fixed-term contract, performance plan, mutual agreement…"
                maxLength={500}
                showCount
              />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="terminationDate"
                  label="Termination Date"
                  rules={[{ required: true, message: 'Required' }]}
                  tooltip="Date the termination is recorded. Drives the Status change."
                >
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="lastWorkingDay"
                  label="Last Working Day"
                  tooltip="The employee's final day on the payroll / in office. Often = Termination Date but may differ for notice periods."
                >
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="removeFromZkDevices" valuePropName="checked">
              <Checkbox>
                Remove user from all linked ZK attendance devices (recommended)
              </Checkbox>
            </Form.Item>
            <Alert
              type="info"
              showIcon
              message="ZK device cleanup is best-effort"
              description="The termination is saved even if a device is offline — failed devices are listed in the success message, and local ZK mappings are always removed."
              style={{ marginBottom: 16 }}
            />
            <Form.Item style={{ marginBottom: 0 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => setTerminateEmployee(null)}>Cancel</Button>
                <Button type="primary" danger htmlType="submit" loading={loading} icon={<StopOutlined />}>
                  Terminate Employee
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Spin>
  );
};

export default EmployeeManagement;
